/* eslint-disable */
import React, { useContext, useRef, useState, useEffect } from "react";
import {
  copyLecturesInAcademicPlan,
  createLecture,
  IsAddedFlagSetTrue,
  updatedIsAddedFlag,
} from "./helper";
import SubjectTreeView from "./treeView/Test";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import AddLectureFrom from "./addedLecture/index";
import { PermissionContext, RolePermissions } from "appContext";
import { Card, CardBody } from "reactstrap";
import LectureListView from "./lectureList/index";
import ALoader from "../../common/a-loader";
import NoLecturesDisplay from "./lectureList/NoLecturesDisplay";
import { checkPermission } from "../../common/utils/methods/commonMethods/utilityMethod";
import { pages } from "../../common/constant";
import { setChapterIsAddedFalse, getLectures } from "./helper";
import CopyLecturesDialog from "./CopyLecturesDialog";
import { successToast } from "../../common/utils/methods/toasterFunctions/function";

const ensureArray = (arr) => (Array.isArray(arr) ? arr : []);

/**
 * Recursively reset isAdded/added on a node AND every descendant.
 * Needed when a Sub-Topic is deleted so its Sub-Sub-Topic children
 * also lose their "Added" badge in the left-panel tree.
 */
const deepResetIsAdded = (node) => {
  if (!node) return node;
  node.isAdded = false;
  node.added = false;
  if (Array.isArray(node.children)) {
    node.children = node.children.map(deepResetIsAdded);
  }
  return node;
};

const AddLecture = ({ DATA, currentDuration, ...props }) => {
  const [currentLecture, setCurrentLecture] = useState({});

  /**
   * lectureRef always mirrors currentLecture.
   * addHandler and deleteHandler read from this ref so they always see the
   * most-recently committed state, even if React hasn't flushed a prior
   * setState yet (stale-closure / batching fix).
   */
  const lectureRef = useRef({});

  const commitLecture = (val) => {
    lectureRef.current = val;
    setCurrentLecture(val);
  };

  const [lectures, setLectures] = useState([]);
  const [fetchingLectures, setFetchingLectures] = useState(false);
  const [subjectChapters, setSubjectChapters] = useState({});
  const [deletedDataTracking, setDeletedDataTracking] = useState([]);
  const [newAddedNodeTrack, newAddedNodeTrackHandler] = useState([]);
  const [lectureState, setLectureState] = useState("");
  const [copyDialog, setCopyDialog] = useState({ isOpen: false, data: null });
  const [expended, setExpended] = useState([]);

  const { isEdit, setIsEdit } = props;
  const userPermissions = RolePermissions(
    useContext(PermissionContext),
    pages["searchAcademicPlan"]["id"],
  );

  useEffect(() => {
    setSubjectChapters({ ...updatedIsAddedFlag(DATA) });
  }, [DATA]);

  useEffect(() => {
    fetchLectures();
    props?.fetchCourseSyllabusV2(props?.courseId);
    newAddedNodeTrackHandler([]);
    setDeletedDataTracking([]);
    commitLecture({});
  }, [currentDuration, DATA.subject]);

  const fetchLectures = () => {
    if (props?.planId && DATA?.subject) {
      setFetchingLectures(true);
      getLectures(props.planId, DATA.subject, currentDuration).then((res) => {
        setLectures(res?.data?.data);
        setLectureState(res?.state);
        setFetchingLectures(false);
      });
    }
  };

  const deleteHandlerTracker = (data) => {
    setDeletedDataTracking((prev) => [...prev, data]);
  };

  const getNewLecture = () => {
    const id = lectures.length + 1;
    return { id_: id, lectureNumber: id, tableOfContent: [] };
  };

  const addLectureHandler = () => commitLecture(getNewLecture());

  const addHandler = (data) => {
    const copyData = { ...data };
    if (isEdit) newAddedNodeTrackHandler((prev) => [...prev, copyData]);

    // Always deep-copy the ref (not the state) to get the freshest lecture.
    const fresh = lectureRef.current;
    let lec = fresh.id_ ? JSON.parse(JSON.stringify(fresh)) : getNewLecture();

    if (!fresh.id_) commitLecture(lec); // prime the ref for next call
    if (!Array.isArray(lec.tableOfContent)) lec.tableOfContent = [];

    if (data.nodeType === "Chapter") {
      if (!lec.tableOfContent.some((c) => c.key === copyData.key)) {
        lec.tableOfContent.push(
          IsAddedFlagSetTrue(copyData, setExpended, expended),
        );
      }
      commitLecture(lec);
      return;
    }

    // data.parentNode = chapter.key
    if (data.nodeType === "Topic") {
      const chInLec = lec.tableOfContent.find((c) => c.key === data.parentNode);
      if (chInLec) {
        chInLec.children = ensureArray(chInLec.children);
        if (!chInLec.children.some((t) => t.key === copyData.key)) {
          chInLec.children.push({ ...IsAddedFlagSetTrue(copyData) });
        }
      } else {
        const subj = JSON.parse(JSON.stringify(subjectChapters));
        const chFromSubj = subj.tableOfContent.find(
          (c) => c.key === data.parentNode,
        );
        if (chFromSubj) {
          chFromSubj.isAdded = true;
          lec.tableOfContent.push({
            ...chFromSubj,
            children: [{ ...IsAddedFlagSetTrue(copyData) }],
          });
          setSubjectChapters(subj);
        }
      }
      commitLecture(lec);
      return;
    }

    // Sub-Topic
    // data.topicParentId = chapter.key
    // data.parentNode    = topic.key
    if (data.nodeType === "Sub-Topic") {
      const chInLec = lec.tableOfContent.find(
        (c) => c.key === data.topicParentId,
      );

      if (chInLec) {
        chInLec.children = ensureArray(chInLec.children);
        const topicInLec = chInLec.children.find(
          (t) => t.key === data.parentNode,
        );

        if (topicInLec) {
          // Topic already present — just append sub-topic (no duplicate)
          topicInLec.children = ensureArray(topicInLec.children);
          if (!topicInLec.children.some((st) => st.key === copyData.key)) {
            topicInLec.children.push({ ...copyData });
          }
        } else {
          // Chapter present but topic is not — add slim topic with this sub-topic
          const subj = JSON.parse(JSON.stringify(subjectChapters));
          const tFromSubj = subj.tableOfContent
            .find((c) => c.key === data.topicParentId)
            ?.children?.find((t) => t.key === data.parentNode);
          if (tFromSubj) {
            chInLec.children.push({
              ...tFromSubj,
              isAdded: true,
              children: [{ ...copyData }],
            });
          }
        }
      } else {
        // Chapter not in lecture at all — build full chain
        const subj = JSON.parse(JSON.stringify(subjectChapters));
        const chFromSubj = subj.tableOfContent.find(
          (c) => c.key === data.topicParentId,
        );
        if (chFromSubj) {
          const tFromSubj = chFromSubj.children?.find(
            (t) => t.key === data.parentNode,
          );
          chFromSubj.isAdded = true;
          if (tFromSubj) tFromSubj.isAdded = true;
          lec.tableOfContent.push({
            ...chFromSubj,
            children: tFromSubj
              ? [{ ...tFromSubj, isAdded: true, children: [{ ...copyData }] }]
              : [],
          });
          setSubjectChapters(subj);
        }
      }
      commitLecture(lec);
      return;
    }

    // Sub-Sub-Topic
    // data.chapterParentId = chapter.key
    // data.topicParentId   = topic.key
    // data.parentNode      = subtopic (Sub-Topic) key
    if (data.nodeType === "Sub-Sub-Topic") {
      const {
        parentNode, // Sub-Topic key
        topicParentId, // Topic key
        chapterParentId, // Chapter key
        ...cleanData
      } = copyData;

      // ① Try to slot into the existing chain
      let inserted = false;
      lec.tableOfContent.forEach((chapter) => {
        if (chapter.key === chapterParentId) {
          ensureArray(chapter.children).forEach((topic) => {
            if (topic.key === topicParentId) {
              ensureArray(topic.children).forEach((subtopic) => {
                if (subtopic.key === parentNode) {
                  subtopic.children = ensureArray(subtopic.children);
                  if (!subtopic.children.some((s) => s.key === cleanData.key)) {
                    subtopic.children.push({ ...cleanData });
                  }
                  inserted = true;
                }
              });
            }
          });
        }
      });

      // Partial/full chain missing — MERGE into existing chapter/topic,
      //    NEVER push a new chapter (that produces duplicate "Life Processes").
      if (!inserted) {
        const subj = JSON.parse(JSON.stringify(subjectChapters));
        const chFromSubj = subj.tableOfContent.find(
          (c) => c.key === chapterParentId,
        );
        const tFromSubj = chFromSubj?.children?.find(
          (t) => t.key === topicParentId,
        );
        const stFromSubj = tFromSubj?.children?.find(
          (st) => st.key === parentNode,
        );

        const newSubtopicNode = {
          ...(stFromSubj || { key: parentNode }),
          isAdded: true,
          children: [{ ...cleanData }],
        };

        const existingChapter = lec.tableOfContent.find(
          (c) => c.key === chapterParentId,
        );

        if (existingChapter) {
          existingChapter.children = ensureArray(existingChapter.children);
          const existingTopic = existingChapter.children.find(
            (t) => t.key === topicParentId,
          );

          if (existingTopic) {
            // Topic found but the Sub-Topic (parentNode) was deleted earlier.
            // Push the Sub-Topic back with its new Sub-Sub-Topic child.
            existingTopic.children = ensureArray(existingTopic.children);
            if (!existingTopic.children.some((st) => st.key === parentNode)) {
              existingTopic.children.push(newSubtopicNode);
            }
          } else {
            // Chapter exists but topic is absent — add topic with subtopic
            existingChapter.children.push({
              ...(tFromSubj || { key: topicParentId }),
              isAdded: true,
              children: [newSubtopicNode],
            });
          }
        } else {
          // Neither chapter nor topic present — build full chain
          lec.tableOfContent.push({
            ...(chFromSubj || { key: chapterParentId }),
            isAdded: true,
            children: [
              {
                ...(tFromSubj || { key: topicParentId }),
                isAdded: true,
                children: [newSubtopicNode],
              },
            ],
          });
        }

        setSubjectChapters(subj);
      }

      commitLecture(lec);
      return;
    }
  };

  // deleteHandler
  const deleteHandler = (nodeData) => {
    //  Chapter
    if (nodeData.nodeType.toLowerCase() === "chapter") {
      const lec = JSON.parse(JSON.stringify(lectureRef.current));
      lec.tableOfContent = lec.tableOfContent.filter(
        (c) => c.key !== nodeData.key,
      );

      const subj = JSON.parse(JSON.stringify(subjectChapters));
      subj.tableOfContent?.forEach((c) => {
        if (c.key === nodeData.key) {
          if (isEdit) deleteHandlerTracker(c);
          deepResetIsAdded(c);
        }
      });

      commitLecture(lec);
      setSubjectChapters(subj);
    }

    //  Topic
    // nodeData.parentNode = chapter.key
    if (nodeData.nodeType.toLowerCase() === "topic") {
      const lec = JSON.parse(JSON.stringify(lectureRef.current));
      lec.tableOfContent = lec.tableOfContent?.map((chapter) => {
        if (chapter.key === nodeData.parentNode) {
          chapter.children = chapter.children?.filter(
            (t) => t.key !== nodeData.key,
          );
        }
        return chapter;
      });

      const subj = JSON.parse(JSON.stringify(subjectChapters));
      subj.tableOfContent?.forEach((chapter) => {
        if (chapter.key === nodeData.parentNode) {
          chapter.children = chapter.children?.map((topic) => {
            if (topic.key === nodeData.key) {
              if (isEdit) deleteHandlerTracker(topic);
              // deepResetIsAdded clears topic + ALL sub-topic + sub-sub-topic flags
              return deepResetIsAdded({ ...topic });
            }
            return topic;
          });
        }
      });

      commitLecture(lec);
      setSubjectChapters(subj);
    }

    // Sub-Topic
    // nodeData: { ...subtopic, topicParentId: chapter.key }
    // nodeData.parentNode = topic.key
    if (nodeData.nodeType.toLowerCase() === "sub-topic") {
      const lec = JSON.parse(JSON.stringify(lectureRef.current));
      lec.tableOfContent = lec.tableOfContent?.map((chapter) => {
        if (chapter.key === nodeData.topicParentId) {
          chapter.children = chapter.children?.map((topic) => {
            if (topic.key === nodeData.parentNode) {
              topic.children = topic.children?.filter((subtopic) => {
                if (subtopic.key === nodeData.key && isEdit)
                  deleteHandlerTracker(subtopic);
                return subtopic.key !== nodeData.key;
              });
            }
            return topic;
          });
        }
        return chapter;
      });

      const subj = JSON.parse(JSON.stringify(subjectChapters));
      subj.tableOfContent?.forEach((chapter) => {
        if (chapter.key === nodeData.topicParentId) {
          chapter.children = chapter.children?.map((topic) => {
            if (topic.key === nodeData.parentNode) {
              topic.children = topic.children?.map((subtopic) => {
                if (subtopic.key === nodeData.key) {
                  // deepResetIsAdded resets Photosynthesis AND Stomata/Site flags
                  return deepResetIsAdded({ ...subtopic });
                }
                return subtopic;
              });
            }
            return topic;
          });
        }
      });

      commitLecture(lec);
      setSubjectChapters(subj);
    }

    // Sub-Sub-Topic
    // nodeData: { ...subsubtopic, parentNode: subtopic.key,
    //             topicParentId: topic.key, chapterParentId: chapter.key }
    if (nodeData.nodeType === "Sub-Sub-Topic") {
      const lec = JSON.parse(JSON.stringify(lectureRef.current));

      lec.tableOfContent.forEach((chapter) => {
        if (chapter.key === nodeData.chapterParentId) {
          chapter.children?.forEach((topic) => {
            if (topic.key === nodeData.topicParentId) {
              topic.children?.forEach((subtopic) => {
                if (subtopic.key === nodeData.parentNode) {
                  subtopic.children = subtopic.children?.filter(
                    (s) => s.key !== nodeData.key,
                  );
                  if (!subtopic.children?.length) subtopic.isAdded = false;
                }
              });
              const anyActive = topic.children?.some(
                (st) => st.isAdded || ensureArray(st.children).length > 0,
              );
              if (!anyActive) topic.isAdded = false;
            }
          });
          const anyActive = chapter.children?.some(
            (t) =>
              t.isAdded ||
              t.children?.some(
                (st) => st.isAdded || ensureArray(st.children).length > 0,
              ),
          );
          if (!anyActive) chapter.isAdded = false;
        }
      });

      const subj = JSON.parse(JSON.stringify(subjectChapters));
      subj.tableOfContent?.forEach((chapter) => {
        if (chapter.key === nodeData.chapterParentId) {
          chapter.children?.forEach((topic) => {
            if (topic.key === nodeData.topicParentId) {
              topic.children?.forEach((subtopic) => {
                if (subtopic.key === nodeData.parentNode) {
                  subtopic.children?.forEach((subsub) => {
                    if (subsub.key === nodeData.key) subsub.isAdded = false;
                  });
                  if (!subtopic.children?.some((s) => s.isAdded))
                    subtopic.isAdded = false;
                }
              });
            }
          });
        }
      });

      commitLecture(lec);
      setSubjectChapters(subj);
    }
  };

  //  save / edit helpers
  const onCurrentLectureDel = () => {
    commitLecture({});
    setIsEdit(false);
    props?.fetchCourseSyllabusV2(props?.courseId);
    setDeletedDataTracking([]);
    newAddedNodeTrackHandler([]);
  };

  const onSave = () => {
    props.createLecture(
      {
        ...currentLecture,
        deletedTableOfContent: deletedDataTracking,
        newAddedTableOfContent: newAddedNodeTrack,
        lectureState,
      },
      onCurrentLectureDel,
      fetchLectures,
    );
  };

  const toggleChange = (lecture, isActive) => {
    setIsEdit(true);
    props.createLecture(
      { ...lecture, isActive, lectureState },
      onCurrentLectureDel,
      fetchLectures,
      true,
    );
  };

  const onEditHandler = (lecture, count) => {
    setIsEdit(true);
    const tempData = JSON.parse(JSON.stringify(subjectChapters));
    lecture?.tableOfContent?.forEach((item) => {
      tempData?.tableOfContent?.forEach((subLec) => {
        if (item.key === subLec.key) {
          subLec.isAdded = true;
          item.children?.forEach((topic) => {
            subLec.children?.forEach((el) => {
              if (el.key === topic.key) {
                el.isAdded = true;
                topic.children?.forEach((subtopic) => {
                  el.children?.forEach((lastNode) => {
                    if (subtopic.key === lastNode.key) {
                      lastNode.isAdded = true;
                      subtopic.children?.forEach((subsub) => {
                        lastNode.children?.forEach((deepNode) => {
                          if (subsub.key === deepNode.key)
                            deepNode.isAdded = true;
                        });
                      });
                    }
                  });
                });
              }
            });
          });
        }
      });
    });
    setSubjectChapters(tempData);
    commitLecture({ ...lecture, id_: count });
  };

  const handleCopyLectures = async ({ from, to }) => {
    const payload = {
      durationFrom: from.value,
      durationTo: to.value,
      subject: DATA?.subject,
      academicPlanId: props.planId,
      courseId: props?.courseId,
    };
    const response = await copyLecturesInAcademicPlan(payload);
    if (response?.code === 200)
      successToast(
        `Lectures copied from ${from.label} to ${to.label} successfully.`,
      );
    if (response?.code === 200 && currentDuration == to.value) fetchLectures();
  };

  //  render
  return (
    <div className="d-flex ml-2 mr-2 mb-2" style={{ alignItems: "flex-start" }}>
      {fetchingLectures && <ALoader position={"fixed"} />}
      <Card className="">
        <CardBody>
          <div className="d-flex justify-content-between mb-4">
            <h3 className="heading-4">{DATA?.subject} syllabus</h3>
            {checkPermission(userPermissions, "C") && (
              <>
                <AButton
                  onClick={addLectureHandler}
                  disabled={currentLecture.id_}
                  variant="primary_filled"
                >
                  Add Lecture
                </AButton>
                <AButton
                  onClick={() => setCopyDialog({ isOpen: true })}
                  disabled={currentLecture.id_}
                  variant="primary_filled"
                >
                  Copy Lectures From
                </AButton>
              </>
            )}
          </div>
          <SubjectTreeView
            subjectChapters={subjectChapters}
            setSubjectChapters={setSubjectChapters}
            addHandler={addHandler}
            setExpended={setExpended}
            expended={expended}
          />
        </CardBody>
      </Card>

      <div className="d-flex flex-column ml-2" style={{ flex: 1 }}>
        <AddLectureFrom
          isEdit={isEdit}
          lectures={lectures}
          currentLecture={currentLecture}
          setCurrentLecture={setCurrentLecture}
          onLectureSave={onSave}
          lectureState={lectureState}
          expended={expended}
          onCurrentLectureDel={onCurrentLectureDel}
          deleteHandler={deleteHandler}
          isLoading={props?.isLoading}
        />
        {lectures?.map((lecture, index) => (
          <LectureListView
            key={lecture.id || index}
            lectureData={lecture}
            counter={index + 1}
            onEditHandler={onEditHandler}
            toggleChange={toggleChange}
          />
        ))}
        {!Object.entries(currentLecture).length && !lectures?.length && (
          <NoLecturesDisplay listView />
        )}
      </div>

      {copyDialog.isOpen && (
        <CopyLecturesDialog
          copyDialog={copyDialog}
          setCopyDialog={setCopyDialog}
          handleCopyLectures={handleCopyLectures}
        />
      )}
    </div>
  );
};

export default AddLecture;
